import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'
import { accessFields, type AccessOption, type DocumentAccess } from './easeedeskSolutions'

/**
 * Easeedesk → Self-Service, calea de CITIRE.
 * Perechile vechi: `self_service` (index), `self_service/preview/<id>`,
 * `self_service/categories`, `self_service/searches`.
 *
 * `apiGet` pune singur `/api/v2` in fata caii - de-aia caile de aici incep de
 * la `/easeedesk`.
 *
 * NU EXISTA NICIUN `apiPost` AICI, si asta nu e o scapare: `save`, `remove` si
 * `save_category` au ramas in portalul vechi, impreuna cu editorul CKEditor din
 * formular. Vezi `SelfServicePage.tsx`.
 */

export interface SelfServiceCategory {
  ID: number
  name: string
  sort_order: number
  /** 1 aprinsa, 0 stinsa. Lista le poarta pe amandoua. */
  status: number
}

export interface SelfServiceArticle {
  ID: number
  title: string
  summary: string
  category_id: number
  /** `null` = "no category", si nu e acelasi lucru cu o categorie fara nume. */
  category_name: string | null
  lang: string
  /** `null` = nimeni n-a masurat. Zero ar fi promis "instantaneu". */
  self_serve_minutes: number | null
  published: number
}

export interface SelfServiceFilters {
  q: string
  category_id: number
  /** `''` = si publicate si nepublicate; `'1'` / `'0'` = numai unele. */
  published: string
}

export interface SelfServicePage {
  title: string
  moduleUrl: string
  /** `easeedesk.solutions_articles.edit` - scrisul e in portalul vechi. */
  canWrite: boolean
  /** Tabelele exista? Migrarea poate sa nu fi rulat inca. */
  storage: boolean
  filters: SelfServiceFilters
  categories: SelfServiceCategory[]
  articles: SelfServiceArticle[]
  /** Rolurile si oamenii de bifat pe un articol - goale pentru cine doar citeste. */
  roles?: AccessOption[]
  people?: AccessOption[]
}

export interface SelfServiceArticleDetail extends SelfServiceArticle {
  /**
   * HTML DE INCREDERE, scris in CKEditor de un operator al portalului.
   * `Self_service::save()` sare peste filtrul XSS chiar pe campul asta, si
   * `preview.php` il randa brut. Vezi `SelfServicePage.tsx`.
   */
  body: string
  /** "Estimated fix: 10 minutes", compusa in PHP; `''` cand nu se stie. */
  minutes_label: string
  /** Cine vede articolul (numai pentru cine scrie); gol = mosteneste categoria. */
  access?: DocumentAccess
}

export interface SelfServiceCategoriesPage {
  moduleUrl: string
  canWrite: boolean
  storage: boolean
  categories: SelfServiceCategory[]
}

export interface SelfServiceSearchSummary {
  total: number
  people: number
  /** Cate cautari s-au terminat cu mainile goale - cifra care conteaza. */
  empty: number
  outcomes: Record<string, number>
}

export interface SelfServiceUnanswered {
  query: string
  times: number
  last_seen: string
}

export interface SelfServiceFailed {
  article_id: number
  /** `null` = sters, sau al altui client. */
  title: string | null
  times: number
  last_seen: string
}

export interface SelfServiceRecent {
  query: string
  hits_articles: number
  hits_catalog: number
  outcome: number
  created_at: string
}

export interface SelfServiceSearchesPage {
  title: string
  moduleUrl: string
  days: number
  periods: number[]
  outcomeNames: Record<string, string>
  storage: boolean
  /** Exista coloana `article_id`? A venit mai tarziu decat tabela. */
  hasArticles: boolean
  summary: SelfServiceSearchSummary
  unanswered: SelfServiceUnanswered[]
  failed: SelfServiceFailed[]
  recent: SelfServiceRecent[]
}

export interface SelfServiceQuery {
  q: string
  category: number
  /** `''` = amandoua, ca in ecranul vechi. */
  published: string
}

/** Aceiasi trei parametri pe care ii trimitea formularul `GET` din macheta. */
export function selfServiceParams(filters: SelfServiceQuery): URLSearchParams {
  const p = new URLSearchParams()
  if (filters.q !== '') p.set('q', filters.q)
  if (filters.category > 0) p.set('category', String(filters.category))
  if (filters.published !== '') p.set('published', filters.published)
  return p
}

export function selfServicePageQuery(filters: SelfServiceQuery) {
  const params = selfServiceParams(filters).toString()
  return queryOptions({
    queryKey: ['easeedesk', 'self-service', filters],
    queryFn: () => apiGet<SelfServicePage>(`/easeedesk/self-service${params === '' ? '' : `?${params}`}`),
  })
}

/** Panoul de previzualizare: se cere numai cand fereastra chiar se deschide. */
export function selfServiceArticleQuery(id: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'self-service', 'article', id],
    queryFn: () => apiGet<SelfServiceArticleDetail>(`/easeedesk/self-service/articles/${id}`),
    enabled: id > 0,
  })
}

export function selfServiceCategoriesQuery() {
  return queryOptions({
    queryKey: ['easeedesk', 'self-service', 'categories'],
    queryFn: () => apiGet<SelfServiceCategoriesPage>('/easeedesk/self-service/categories'),
  })
}

export function selfServiceSearchesQuery(days: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'self-service', 'searches', days],
    queryFn: () => apiGet<SelfServiceSearchesPage>(`/easeedesk/self-service/searches?days=${days}`),
  })
}

/* -------------------------------------------------------------------------
 * SCRIERILE - `Self_service::save`, `remove`, `save_category`.
 * ---------------------------------------------------------------------- */

/** `{status, message, id}` ca modelele vechi; ERROR vine cu 422, deci ajunge ca `ApiError.message`. */
export interface SelfServiceWriteReply {
  status: 'OK' | 'ERROR'
  message: string
  id?: number
}

export interface SelfServiceArticleForm {
  ID: number
  category_id: number
  lang: string
  title: string
  summary: string
  /** HTML de editor; NU se curata pe server, ca in portal. */
  body: string
  /** Gol = nimeni n-a masurat; `''` si `0` nu sunt acelasi lucru. */
  self_serve_minutes: string
  published: boolean
  /** Lipsa = regula ramane cum era. */
  access?: DocumentAccess
}

export function saveSelfServiceArticle(form: SelfServiceArticleForm): Promise<SelfServiceWriteReply> {
  return apiPost<SelfServiceWriteReply>('/easeedesk/self-service/articles', {
    ID: String(form.ID),
    category_id: String(form.category_id),
    lang: form.lang,
    title: form.title,
    summary: form.summary,
    body: form.body,
    self_serve_minutes: form.self_serve_minutes,
    published: form.published ? '1' : '0',
    ...(form.access ? accessFields(form.access) : {}),
  })
}

export function removeSelfServiceArticle(id: number): Promise<SelfServiceWriteReply> {
  return apiPost<SelfServiceWriteReply>('/easeedesk/self-service/articles/remove', { ID: String(id) })
}

export function saveSelfServiceCategory(category: { ID: number; name: string; sort_order: number; status: boolean }): Promise<SelfServiceWriteReply> {
  return apiPost<SelfServiceWriteReply>('/easeedesk/self-service/categories', {
    ID: String(category.ID),
    name: category.name,
    sort_order: String(category.sort_order),
    status: category.status ? '1' : '0',
  })
}
